// AI 金鑰登錄台(AI key registry)資料層。
//
// 平台集中管理各 consumer(LLM API 金鑰消費端)的金鑰。契約 v4 有三個 consumer,分兩組:
//   group=admin      — admin              → admin 管理助理(features/assistant/AssistantWidget)用
//   group=dev-portal — dev-portal-assistant / dev-portal-diagnostics
//                      → 開發者入口消費端(已接線:restful /v3/dev/assistant/config|chat 與
//                        diagnostics_enabled 掛此兩槽;未設金鑰=portal 端功能停用)
//
// 端點(restful `feat/assistant-key-config` 分支同時在建,契約 v4):
//   GET    /v3/admin/ai-keys              — {keys:[{consumer,group,group_label,label,key_configured,key_hint,source,enabled,can_edit}]}(CanAdminRead)
//   PUT    /v3/admin/ai-keys/{consumer}   — body {api_key} → 回該 consumer 更新後狀態(僅 platform-owner)
//   DELETE /v3/admin/ai-keys/{consumer}   — 回該 consumer 更新後狀態(退回 env fallback 或未設定)
//   POST   /v3/admin/ai-keys/{consumer}/test — 打一發即時探測 → {ok,model,latency_ms,error?}(僅 platform-owner)
//
// **只寫不讀**鐵則:回應永不含明文金鑰,前端只吃遮罩末四碼 `key_hint`。認證/401 refresh 沿用
// assistantApi 的 baseHeaders/readError(console 既有 Bearer + X-Client CSRF gate)。config 端點回
// **裸 JSON**、容忍 restful `{data}` 信封(兩種都讀)。

import { config } from '../../config';
import { ApiError, refreshAccessToken, tokenStore } from '../../lib/api';
import { baseHeaders, readError } from './assistantApi';

const AI_KEYS_PREFIX = '/v3/admin/ai-keys';

/** 金鑰來源:UI 登錄台寫入(stored)、後端環境變數(env)、或尚未設定(null)。 */
export type AiKeySource = 'stored' | 'env' | null;

/** 登錄台一筆 consumer 的金鑰狀態(**永不含明文**,只帶遮罩 key_hint)。 */
export interface AiKeyEntry {
  /** 穩定的 consumer 識別碼(路徑用),如 `admin` / `dev-portal-assistant`。 */
  consumer: string;
  /** 分組鍵(後端權威),如 `admin` / `dev-portal`;缺省時由 grouping 層自 consumer 推導。 */
  group: string;
  /** 分組顯示名,如「Admin 管理助理」「開發者入口 Dev Portal」;缺省時由 grouping 層補。 */
  group_label: string;
  /** 顯示名,如「管理助理」「開發者入口 · AI 助理」。 */
  label: string;
  /** 是否已設定金鑰(stored 或 env 皆算)。 */
  key_configured: boolean;
  /** 遮罩提示(末四碼,如 `••••1234`);未設定為空字串。 */
  key_hint: string;
  /** 目前金鑰來源。 */
  source: AiKeySource;
  /** 此 consumer 的 AI 功能是否啟用。 */
  enabled: boolean;
  /** 目前登入者能否設定此 key(後端權威;僅 platform-owner 為 true)。 */
  can_edit: boolean;
}

function aiKeysUrl(path = ''): string {
  return config.apiBase + AI_KEYS_PREFIX + path;
}

function parseSource(v: unknown): AiKeySource {
  return v === 'stored' ? 'stored' : v === 'env' ? 'env' : null;
}

/**
 * 把後端一筆 consumer 狀態轉成受檢的 AiKeyEntry。
 * **只讀已知的非機密欄位**:即便後端誤帶明文(如 `api_key`),也絕不會流入前端型別。
 */
export function parseAiKeyEntry(raw: unknown): AiKeyEntry {
  const r = (raw ?? {}) as Record<string, unknown>;
  const consumer = typeof r.consumer === 'string' ? r.consumer : '';
  return {
    consumer,
    group: typeof r.group === 'string' ? r.group : '',
    group_label: typeof r.group_label === 'string' ? r.group_label : '',
    label: typeof r.label === 'string' && r.label ? r.label : consumer,
    key_configured: Boolean(r.key_configured),
    key_hint: typeof r.key_hint === 'string' ? r.key_hint : '',
    source: parseSource(r.source),
    enabled: Boolean(r.enabled),
    can_edit: Boolean(r.can_edit),
  };
}

/** 測試探測結果(POST /{consumer}/test);**永不含明文**,只帶 ok / model / latency / error。 */
export interface AiKeyTestResult {
  /** 探測是否成功(後端實打一發 LLM 呼叫)。 */
  ok: boolean;
  /** 探測所用模型(後端回;失敗時可能為空)。 */
  model: string;
  /** 往返延遲毫秒(失敗時可能為 null)。 */
  latency_ms: number | null;
  /** 失敗原因(成功時為 null)。 */
  error: string | null;
}

/** 受檢投影測試結果:只讀已知非機密欄位(即便後端誤帶 key 也不流入)。 */
export function parseAiKeyTestResult(raw: unknown): AiKeyTestResult {
  const r = (raw ?? {}) as Record<string, unknown>;
  return {
    ok: Boolean(r.ok),
    model: typeof r.model === 'string' ? r.model : '',
    latency_ms: typeof r.latency_ms === 'number' ? r.latency_ms : null,
    error: typeof r.error === 'string' && r.error ? r.error : null,
  };
}

/**
 * 共用請求:打端點、401 → refresh-and-retry 一次、讀 `{error}`/`{detail}` 錯誤信封,
 * 成功回**已剝信封**的裸 body(`data ?? body`)。呼叫端各自 parse。
 */
async function request(
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  path: string,
  body?: unknown,
  signal?: AbortSignal,
  _retried = false
): Promise<unknown> {
  const res = await fetch(aiKeysUrl(path), {
    method,
    headers: baseHeaders(),
    body: body !== undefined ? JSON.stringify(body) : undefined,
    signal,
  });

  if (res.status === 401 && !_retried) {
    try {
      await refreshAccessToken();
    } catch {
      tokenStore.clear();
      throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', res.headers.get('X-Request-Id'));
    }
    return request(method, path, body, signal, true);
  }

  const requestId = res.headers.get('X-Request-Id');
  const parsed = (await res.json().catch(() => ({}))) as { data?: unknown } & Record<string, unknown>;

  if (res.status === 401) {
    tokenStore.clear();
    throw new ApiError(401, 'UNAUTHORIZED', '登入逾期，請重新登入', requestId);
  }
  if (!res.ok) throw readError(res.status, parsed, requestId);

  return parsed.data ?? parsed;
}

/** GET /v3/admin/ai-keys → consumer 金鑰列表(裸 `{keys}` 或 `{data:{keys}}` 皆可)。 */
export async function fetchAiKeys(signal?: AbortSignal): Promise<AiKeyEntry[]> {
  const body = (await request('GET', '', undefined, signal)) as { keys?: unknown } | unknown[];
  const rawList = Array.isArray(body) ? body : Array.isArray((body as { keys?: unknown }).keys) ? (body as { keys: unknown[] }).keys : [];
  return rawList.map(parseAiKeyEntry);
}

/** PUT /v3/admin/ai-keys/{consumer} body {api_key} → 該 consumer 更新後狀態。 */
export async function updateAiKey(consumer: string, apiKey: string, signal?: AbortSignal): Promise<AiKeyEntry> {
  const body = await request('PUT', '/' + encodeURIComponent(consumer), { api_key: apiKey }, signal);
  return parseAiKeyEntry(body);
}

/** DELETE /v3/admin/ai-keys/{consumer} → 該 consumer 更新後狀態(退回 env fallback 或未設定)。 */
export async function deleteAiKey(consumer: string, signal?: AbortSignal): Promise<AiKeyEntry> {
  const body = await request('DELETE', '/' + encodeURIComponent(consumer), undefined, signal);
  return parseAiKeyEntry(body);
}

/** POST /v3/admin/ai-keys/{consumer}/test → 即時探測結果(僅 platform-owner)。 */
export async function testAiKey(consumer: string, signal?: AbortSignal): Promise<AiKeyTestResult> {
  const body = await request('POST', '/' + encodeURIComponent(consumer) + '/test', undefined, signal);
  return parseAiKeyTestResult(body);
}
